import React from 'react';
import { supabase } from '../../lib/supabaseClient';

const MIN_PASSWORD_LENGTH = 8;

// Shown after an invite / signup / recovery email link, when Supabase has
// created a session but the user has no usable password yet. Blocks the
// dashboard until supabase.auth.updateUser({ password }) succeeds, then calls
// onDone() to hand the (now fully valid) session back to the dashboard.
export const SetPasswordScreen = ({ onDone }) => {
  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm]   = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [error, setError]   = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = async () => {
    if (!password || !confirm) {
      setError('Please enter and confirm your new password.');
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    setError('');
    const { error: updateError } = await supabase.auth.updateUser({ password });
    if (updateError) {
      setError(updateError.message || 'Could not set your password. Please try again.');
      setLoading(false);
      return;
    }
    // Session is already valid — hand control back to the dashboard.
    onDone();
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow:wght@400;500&family=Barlow+Condensed:wght@600;700&display=swap');
        .sp-wrap {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #0d0f14;
          font-family: 'Barlow', sans-serif;
        }
        .sp-card {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(49,220,186,0.2);
          border-radius: 20px;
          padding: 52px 44px;
          width: 100%;
          max-width: 420px;
          text-align: center;
          box-shadow: 0 0 60px rgba(49,220,186,0.06), 0 24px 48px rgba(0,0,0,0.4);
          backdrop-filter: blur(12px);
        }
        .sp-logo-wrap { position: relative; display: inline-block; margin-bottom: 32px; }
        .sp-logo-glow {
          position: absolute; top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          width: 140px; height: 140px;
          background: radial-gradient(circle, rgba(49,220,186,0.25) 0%, transparent 70%);
          border-radius: 50%; pointer-events: none;
        }
        .sp-logo-img {
          width: 80px; height: 80px; object-fit: contain;
          position: relative; z-index: 1;
          filter: drop-shadow(0 0 12px rgba(49,220,186,0.5));
        }
        .sp-title {
          font-family: 'Barlow Condensed', sans-serif;
          font-size: 32px; font-weight: 700; color: #fff;
          margin: 0 0 8px; letter-spacing: 0.02em;
        }
        .sp-sub { color: rgba(255,255,255,0.4); font-size: 14px; margin: 0 0 36px; }
        .sp-input {
          width: 100%; padding: 14px 18px;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px; color: #fff;
          font-family: 'Barlow', sans-serif; font-size: 16px;
          margin-bottom: 14px; box-sizing: border-box;
          transition: border-color 0.2s; outline: none;
        }
        .sp-input:focus { border-color: #31DCBA; }
        .sp-input::placeholder { color: rgba(255,255,255,0.25); }
        .sp-password-field { position: relative; }
        .sp-input--password { padding-right: 48px; }
        .sp-eye-btn {
          position: absolute; top: 0; bottom: 14px; right: 0;
          width: 48px; display: flex; align-items: center; justify-content: center;
          background: none; border: none; padding: 0; cursor: pointer;
          color: rgba(255,255,255,0.4); transition: color 0.2s;
        }
        .sp-eye-btn:hover { color: #31DCBA; }
        .sp-eye-btn:focus-visible { color: #31DCBA; outline: none; }
        .sp-btn {
          width: 100%; padding: 14px;
          background: #31DCBA; color: #0d0f14;
          font-family: 'Barlow Condensed', sans-serif;
          font-size: 18px; font-weight: 700; letter-spacing: 0.06em;
          text-transform: uppercase; border: none; border-radius: 10px;
          cursor: pointer; transition: opacity 0.2s, transform 0.1s; margin-top: 6px;
        }
        .sp-btn:hover { opacity: 0.9; transform: translateY(-1px); }
        .sp-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
        .sp-error { color: #ff6b6b; font-size: 13px; margin-top: 14px; }
        .sp-footer {
          margin-top: 32px; color: rgba(255,255,255,0.15);
          font-size: 12px; letter-spacing: 0.08em; text-transform: uppercase;
        }
      `}</style>
      <div className="sp-wrap">
        <div className="sp-card">
          <div className="sp-logo-wrap">
            <div className="sp-logo-glow" />
            <img src="/autopilot_logo.png" alt="Autopilot Digital" className="sp-logo-img" />
          </div>
          <h2 className="sp-title">Set Your Password</h2>
          <p className="sp-sub">Create a password to finish setting up your account</p>
          <div className="sp-password-field">
            <input
              className="sp-input sp-input--password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="New password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            />
            <button
              type="button"
              className="sp-eye-btn"
              onClick={() => setShowPassword(s => !s)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              tabIndex={-1}
            >
              {showPassword ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
          <input
            className="sp-input"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="Confirm password"
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
          />
          <button className="sp-btn" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Saving…' : 'Set Password & Continue'}
          </button>
          {error && <div className="sp-error">{error}</div>}
          <div className="sp-footer">Secure Client Access</div>
        </div>
      </div>
    </>
  );
};
